import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import ClearIcon from "@mui/icons-material/Clear";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { checkFileDuplicate, checkGlobalDuplicate, adoptFile } from "../../services/apiService";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const HEADER_COLOR = "#1B2559";
const PRIMARY_COLOR = "#1976d2";
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const formatSize = (bytes) => {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const scanWindowMinutes = process.env.REACT_APP_SHAREPOINT_SCAN_WINDOW_MINUTES;
const scanWindowHours = Math.round(scanWindowMinutes / 60);
const scanWindowDisplay = scanWindowHours > 24
    ? `${Math.round(scanWindowHours / 24)} day${Math.round(scanWindowHours / 24) > 1 ? "s" : ""}`
    : `${scanWindowHours} hour${scanWindowHours > 1 ? "s" : ""}`;

const getStatusChip = (processingStatus) => {
    switch (processingStatus) {
        case "completed":
            return {
                icon: <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />,
                label: "Completed",
                color: "success",
                variant: "filled",
            };
        case "in_progress":
            return {
                icon: <HourglassEmptyIcon sx={{ fontSize: "14px !important" }} />,
                label: "Processing",
                color: "warning",
                variant: "filled",
            };
        default:
            return {
                icon: <ErrorOutlineIcon sx={{ fontSize: "14px !important" }} />,
                label: "Not Processed",
                color: "error",
                variant: "outlined",
            };
    }
};

const COLUMNS = ["File", "Size", "Created", "Modified", "Status", "Action"];

const SharePointSyncDialog = ({
    open,
    loading,
    files,
    error = null,
    onClose,
    onRefresh = null,
    onSendToJob = null,
    sendingFiles = {},
}) => {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [similarityMap, setSimilarityMap] = useState({});
    const [globalMatchMap, setGlobalMatchMap] = useState({});
    // adoptingMap: tracks per-file adoption state triggered by user click
    // values: 'adopting' | 'adopted' | 'failed'
    const [adoptingMap, setAdoptingMap] = useState({});
    const checkedRef = useRef(false);

    const pendingFiles = useMemo(
        () => files.filter((f) => (f.processingStatus || "not_processed") !== "completed"),
        [files]
    );

    const filteredFiles = useMemo(() => {
        if (!search.trim()) return files;
        const q = search.toLowerCase();
        return files.filter((f) => (f.name || "").toLowerCase().includes(q));
    }, [files, search]);

    const pagedFiles = useMemo(
        () => filteredFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredFiles, page, rowsPerPage]
    );

    useEffect(() => {
        if (!open) {
            setSimilarityMap({});
            setGlobalMatchMap({});
            setAdoptingMap({});
            checkedRef.current = false;
            return;
        }
        if (loading) {
            checkedRef.current = false;
            setSimilarityMap({});
            setGlobalMatchMap({});
            setAdoptingMap({});
            return;
        }
        if (checkedRef.current) return;
        if (files.length === 0) return;

        checkedRef.current = true;

        const filesToCheck = files;
        const initial = {};
        filesToCheck.forEach((f) => {
            initial[f.id || f.name] = { checking: true, checkingGlobal: false, matchType: "no_match", matchedFile: null, generatedHash: null, dbContentHash: null };
        });
        setSimilarityMap(initial);

        filesToCheck.forEach(async (f) => {
            const key = f.id || f.name;
            try {
                // Phase 1: per-user duplicate check (also computes generatedHash)
                const result = await checkFileDuplicate({
                    itemId: f.id || undefined,
                    name: f.name || undefined,
                    contentHash: f.content_hash || undefined,
                    downloadURL: f.download_url || undefined,
                });
                const matchType = result?.match_type || "no_match";
                const matchedFile = result?.matched_file || null;
                const generatedHash = result?.generated_hash || null;
                const dbContentHash = matchedFile?.content_hash || null;
                setSimilarityMap((prev) => ({ ...prev, [key]: { ...prev[key], checking: false, checkingGlobal: generatedHash && matchType === "no_match", matchType, matchedFile, generatedHash, dbContentHash } }));

                // Phase 2: global cross-user check — store match for user-initiated adoption
                if (generatedHash && result.match_type === "no_match") {
                    try {
                        const globalResult = await checkGlobalDuplicate(generatedHash);
                        if (globalResult?.match_found) {
                            setGlobalMatchMap((prev) => ({ ...prev, [key]: globalResult }));
                        }
                    } finally {
                        setSimilarityMap((prev) => ({ ...prev, [key]: { ...prev[key], checkingGlobal: false } }));
                    }
                } else {
                    setSimilarityMap((prev) => ({ ...prev, [key]: { ...prev[key], checkingGlobal: false } }));
                }
            } catch {
                setSimilarityMap((prev) => ({ ...prev, [key]: { checking: false, checkingGlobal: false, matchType: "no_match", matchedFile: null, generatedHash: null, dbContentHash: null } }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loading]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearch("");
        setPage(0);
    };

    const handleAdoptFile = async (file, globalMatch) => {
        const key = file.id || file.name;
        setAdoptingMap((prev) => ({ ...prev, [key]: "adopting" }));
        const result = await adoptFile({
            contentHash: globalMatch.content_hash,
            sourceItemId: globalMatch.source_item_id,
            sourceUserId: globalMatch.source_user_id,
            name: file.name,
        });
        if (result?.error) {
            setAdoptingMap((prev) => ({ ...prev, [key]: "failed" }));
        } else {
            setAdoptingMap((prev) => ({ ...prev, [key]: "adopted" }));
            onRefresh?.();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ...POPPINS,
                    fontWeight: 600,
                    color: HEADER_COLOR,
                    pb: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CloudSyncIcon sx={{ color: PRIMARY_COLOR }} />
                    SharePoint — Available Files
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Refresh files">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onRefresh}
                                disabled={loading}
                                aria-label="Refresh files"
                            >
                                {loading
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <RefreshIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose} aria-label="Close dialog">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {/* Loading */}
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {/* Error */}
                {!loading && error && (
                    <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                        <Typography variant="body2" color="error" sx={POPPINS}>
                            {error}
                        </Typography>
                    </Box>
                )}

                {/* Empty — no files at all */}
                {!loading && !error && files.length === 0 && (
                    <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary" sx={POPPINS}>
                            No new files uploaded in the last {scanWindowDisplay}.
                        </Typography>
                    </Box>
                )}

                {/* All files already processed */}
                {!loading && !error && files.length > 0 && pendingFiles.length === 0 && (
                    <Box sx={{ px: 3, py: 1, textAlign: "left" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ ...POPPINS, fontStyle: "italic" }}>
                            All recent files have already been processed.
                        </Typography>
                    </Box>
                )}

                {/* Search + Table */}
                {!loading && !error && files.length > 0 && (
                    <>
                        {/* Search bar */}
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search by file name…"
                                value={search}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleClearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                    sx: { ...POPPINS, fontSize: "0.85rem" },
                                }}
                            />
                        </Box>

                        <TableContainer>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map((col) => (
                                            <TableCell
                                                key={col}
                                                sx={{
                                                    ...POPPINS,
                                                    fontWeight: 700,
                                                    fontSize: "0.78rem",
                                                    color: HEADER_COLOR,
                                                    backgroundColor: "#f5f7fb",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {col}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {filteredFiles.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary" sx={POPPINS}>
                                                    No files matching &ldquo;{search}&rdquo;
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        pagedFiles.map((file, idx) => {
                                            const processingStatus = file.processingStatus || "not_processed";
                                            const statusChip = getStatusChip(processingStatus);
                                            const isNotProcessed = processingStatus === "not_processed";
                                            const isSending = sendingFiles[file.id || file.name] === "sending";

                                            const simState = similarityMap[file.id || file.name] || { checking: false, checkingGlobal: false, matchType: "no_match", matchedFile: null, generatedHash: null, dbContentHash: null };
                                            const isCheckingSimilarity = simState.checking;
                                            const isCheckingGlobal = simState.checkingGlobal;
                                            const hasDuplicate = simState.matchType !== "no_match";
                                            const matchedName = simState.matchedFile?.name || null;
                                            const globalMatch = globalMatchMap[file.id || file.name] || null;
                                            const adoptState = adoptingMap[file.id || file.name] || null;

                                            // Replay: completed file whose content has changed since last processing
                                            const isContentHashChanged =
                                                processingStatus === "completed" &&
                                                simState.generatedHash &&
                                                simState.dbContentHash &&
                                                simState.generatedHash !== simState.dbContentHash;

                                            // Global match: unprocessed file already adopted automatically
                                            const showGlobalMatch = isNotProcessed && !hasDuplicate && !!globalMatch;

                                            const MATCH_LABELS = {
                                                item_id: "Same file already stored",
                                                name: "File with same name already processed",
                                                content_hash: "File with identical content already processed",
                                            };
                                            const actionTooltip = isCheckingSimilarity
                                                ? "Checking for duplicate documents…"
                                                : isCheckingGlobal
                                                    ? "Checking for cross-user matches…"
                                                    : hasDuplicate
                                                        ? `${MATCH_LABELS[simState.matchType] || "Duplicate found"}${matchedName ? `: "${matchedName}"` : ""}`
                                                        : "Send to Document Processing Job";

                                            return (
                                                <TableRow
                                                    key={file.id || idx}
                                                    hover
                                                    sx={{ "&:last-child td": { border: 0 } }}
                                                >
                                                    {/* File name */}
                                                    <TableCell sx={{ maxWidth: 260 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <InsertDriveFileOutlinedIcon
                                                                sx={{ color: PRIMARY_COLOR, fontSize: 18, flexShrink: 0 }}
                                                            />
                                                            <Tooltip title={file.name}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        ...POPPINS,
                                                                        fontWeight: 500,
                                                                        fontSize: "0.82rem",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        maxWidth: 220,
                                                                    }}
                                                                >
                                                                    {file.name || "Unknown"}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Size */}
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ ...POPPINS, fontSize: "0.78rem", color: "#555", whiteSpace: "nowrap" }}>
                                                            {formatSize(file.size_bytes)}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Created */}
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ ...POPPINS, fontSize: "0.78rem", color: "#555", whiteSpace: "nowrap" }}>
                                                            {formatDate(file.created_at)}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Modified */}
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ ...POPPINS, fontSize: "0.78rem", color: "#555", whiteSpace: "nowrap" }}>
                                                            {file.modified_at && file.modified_at !== file.created_at
                                                                ? formatDate(file.modified_at)
                                                                : "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Status */}
                                                    <TableCell>
                                                        <Chip
                                                            icon={statusChip.icon}
                                                            label={statusChip.label}
                                                            size="small"
                                                            color={statusChip.color}
                                                            variant={statusChip.variant}
                                                            sx={{ ...POPPINS, fontSize: "0.68rem" }}
                                                        />
                                                    </TableCell>

                                                    {/* Action */}
                                                    <TableCell align="center">
                                                        {isContentHashChanged && (
                                                            <Tooltip title="File content has changed since last processing. Click to re-process.">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => onSendToJob && onSendToJob(file)}
                                                                        disabled={sendingFiles[file.id || file.name] === "sending"}
                                                                        sx={{ color: "#7b1fa2", p: 0.5 }}
                                                                        aria-label="Re-process updated file"
                                                                    >
                                                                        {sendingFiles[file.id || file.name] === "sending"
                                                                            ? <CircularProgress size={16} color="inherit" />
                                                                            : <ReplayIcon sx={{ fontSize: 20 }} />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        {showGlobalMatch && (
                                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                                {adoptState === "adopting" && (
                                                                    <CircularProgress size={16} sx={{ color: PRIMARY_COLOR }} />
                                                                )}
                                                                {adoptState === "adopted" && (
                                                                    <Tooltip title={`Added from ${globalMatch.source_user_email}. Available in your library.`}>
                                                                        <Chip
                                                                            icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                                                                            label="Added"
                                                                            size="small"
                                                                            color="success"
                                                                            variant="outlined"
                                                                            sx={{ ...POPPINS, fontSize: "0.68rem" }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                                {(adoptState === "failed" || adoptState === null) && (
                                                                    <Tooltip title={
                                                                        adoptState === "failed"
                                                                            ? "Failed to send for processing. Click to retry."
                                                                            : "Send to document processing job"
                                                                        // : `This file was already processed by ${globalMatch.source_user_email}. Click to add it to your library instantly — no processing needed.`
                                                                    }>
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleAdoptFile(file, globalMatch)}
                                                                                disabled={adoptState === "adopting"}
                                                                                sx={{ color: PRIMARY_COLOR, p: 0.5 }}
                                                                                aria-label="Add to library from existing processed version"
                                                                            >
                                                                                <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        )}
                                                        {isNotProcessed && !showGlobalMatch && (
                                                            <Tooltip title={actionTooltip}>
                                                                <span>
                                                                    {hasDuplicate ? (
                                                                        <IconButton
                                                                            size="small"
                                                                            disableRipple
                                                                            sx={{ color: "#f57c00", p: 0.5, cursor: "default" }}
                                                                            aria-label={actionTooltip}
                                                                        >
                                                                            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                                                        </IconButton>
                                                                    ) : (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => onSendToJob && onSendToJob(file)}
                                                                            disabled={isSending || isCheckingSimilarity || isCheckingGlobal}
                                                                            sx={{ color: PRIMARY_COLOR, p: 0.5 }}
                                                                            aria-label="Send to document processing job"
                                                                        >
                                                                            {isSending || isCheckingSimilarity || isCheckingGlobal
                                                                                ? <CircularProgress size={16} color="inherit" />
                                                                                : <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
                                                                            }
                                                                        </IconButton>
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ display: "flex", alignItems: "center", borderTop: "1px solid #e0e0e0" }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    ...POPPINS,
                                    pl: 2,
                                    color: "#f57c00",
                                    fontStyle: "italic",
                                    flexShrink: 0,
                                }}
                            >
                                ⏱ Currently platform supports PPF & PPT documents. Duplicate validation check may take longer for large files.
                            </Typography>
                            <TablePagination
                                component="div"
                                count={filteredFiles.length}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                sx={{ ...POPPINS, fontSize: "0.78rem", ml: "auto" }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

SharePointSyncDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    files: PropTypes.arrayOf(PropTypes.object).isRequired,
    error: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    onSendToJob: PropTypes.func,
    sendingFiles: PropTypes.object,
};

export default SharePointSyncDialog;
